import React, { useEffect, useRef, useState } from "react";
import axios, { CancelToken } from "axios";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { StatusCode } from "api/protocols";
import { GetGuestFacilityStatus } from "api/rpc/2024-04/guest/facility";
import { GetGuestNotifications, GetGuestEvents } from "api/rpc/2024-04/guest/teesheet";

import { showError } from "redux/actions/ui";
import { TFacilityStatus } from "redux/reducers/models/facility";

import { useAppDispatch } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import { formatDate, sortObj } from "helpers/Helpers";

import Calendar from "components/calendar/Calendar";
import CalendarGridDay from "components/calendar/CalendarGridDay";
import { Badge } from "components/badge/Badge";
import DataTable from "components/dataTable/DataTable";
import FacilityStatusInfo from "../pages/guest/Customer/ClientPortal/Calendar/FacilityStatusInfo";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faTrophy } from "@fortawesome/pro-regular-svg-icons";
import { INotification } from "api/rpc/2024-04/customer/teesheet";
import { ConnectedBadge } from "components/badge/ConnectedBadge";
import Markdown from "components/markdown/Markdown";

import "./calendarIFrame.scss";

export default function CalendarIFrame() {
  const { facilityShortName } = useParams<{ facilityShortName: string }>();
  const [calendarDay, setCalendarDay] = useState(new Date());
  const [facilityStatusList, setFacilityStatusList] = useState<TFacilityStatus[]>(undefined);
  const [statusLoadTrigger, setStatusLoadTrigger] = useState(true);
  const [notificationList, setNotificationList] = useState<INotification[]>(undefined);
  const { t } = useTranslation();

  const dayRefs = useRef<HTMLDivElement[]>([]);

  const windowSize = useWindowSize();
  const isMobile = windowSize.width <= MOBILE_WIDTH;
  const smallWindowHeight = windowSize.height < 700;

  const {
    state: statusModal,
    updateModal: updateStatusModal,
    closeModal: closeStatusModal,
  } = useModal({
    activeDate: null as string,
    ref: null as HTMLDivElement,
    statusData: [] as TFacilityStatus[],
    singleNotification: null as INotification,
    type: null as "updates" | "status" | "event",
    event: null as any,
  });

  const [hoveredNotificationId, setHoveredNotificationId] = useState<number | string | null>(null);
  const [eventList, setEventList] = useState<any[]>(undefined);

  const getNotificationKey = (n: INotification | null | undefined) => {
    if (!n) {
      return "";
    }
    return `${n.id || 0}-${n.subject}-${n.start_date || ""}-${n.end_date || ""}`;
  };

  const dispatch = useAppDispatch();

  // Reload when selected facility changes
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (statusLoadTrigger) {
      void loadFacilityStatusList(facilityShortName, calendarDay.getMonth(), source.token);
      void loadNotifications();
      void loadEvents();
    }

    return () => source.cancel();
  }, [facilityShortName, statusLoadTrigger, calendarDay]);

  async function loadFacilityStatusList(shortName: string, month: number, token?: CancelToken) {
    if (facilityStatusList !== undefined) {
      setFacilityStatusList(undefined);
    }

    const res = await GetGuestFacilityStatus(
      { short_name: shortName, month: month + 1 },
      token ? false : true,
      token,
    );

    if (token && token.reason) {
      return;
    }

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading facility details.")));
    }

    setFacilityStatusList(res.status !== StatusCode.OK ? [] : res.data as TFacilityStatus[]);

    if (statusLoadTrigger) {
      setStatusLoadTrigger(false);
    }
  }

  async function loadNotifications() {
    if (notificationList !== undefined) {
      setNotificationList(undefined);
    }

    const currentStartDateString = moment().startOf("month").format("YYYY-MM-DD");
    const currentEndDateString = moment().endOf("month").format("YYYY-MM-DD");

    const res = await GetGuestNotifications(
      { short_name: facilityShortName, start_date: currentStartDateString, end_date: currentEndDateString },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading notifications.")));
    }

    setNotificationList(res.data);
  }

  async function loadEvents() {
    if (eventList !== undefined) {
      setEventList(undefined);
    }

    const currentStartDateString = moment().startOf("month").format("YYYY-MM-DD");
    const currentEndDateString = moment().endOf("month").format("YYYY-MM-DD");

    const res = await GetGuestEvents(
      { short_name: facilityShortName, start_date: currentStartDateString, end_date: currentEndDateString },
      true,
    );

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading events.")));
      return;
    }

    setEventList(res.data as any[]);
  }

  function handleDateChange(date: Date) {
    if (date.getMonth() !== calendarDay.getMonth() || date.getFullYear() !== calendarDay.getFullYear()) {
      setStatusLoadTrigger(true);
    }

    setCalendarDay(date);
  }

  function handleCalendarCellClick(data: TFacilityStatus[], dateOfMonth: number) {
    if (data == null || data.length === 0) {
      return;
    }

    updateStatusModal({
      isOpen: true,
      activeDate: data[0].date,
      ref: dayRefs.current[dateOfMonth],
      statusData: sortObj<TFacilityStatus>(data, "label"),
      type: "status",
    });
  }

  function handleNotificationClick(notification: INotification, dateOfMonth: number) {
    updateStatusModal({
      isOpen: true,
      activeDate: notification.start_date,
      ref: dayRefs.current[dateOfMonth],
      singleNotification: notification,
      type: "updates",
    });
  }

  function handleEventClick(event: any, dateOfMonth: number) {
    updateStatusModal({
      isOpen: true,
      activeDate: event.date,
      ref: dayRefs.current[dateOfMonth],
      event: event,
      type: "event",
    });
  }

  return (
    <div>
      <Calendar
        fillMonthViewport={true}
        selectedDate={calendarDay}
        setSelectedDate={date => handleDateChange(date)}
        loading={facilityStatusList === undefined}
        renderGridItem={(dateOfMonth: number, formattedDate: string) => {
          const data = facilityStatusList.filter(val => val.date === formattedDate);
          const notifications = notificationList
            ?.filter(
              notification =>
                moment(notification.start_date).isSameOrBefore(moment(formattedDate)) &&
                moment(notification.end_date).isSameOrAfter(moment(formattedDate)),
            )
            .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
          const eventsOnDay = eventList?.filter(val => val.date === formattedDate) || [];

          return (
            <CalendarGridDay
              key={formattedDate}
              dayOfMonth={dateOfMonth}
              dayOfMonthClick={() => (isMobile ? handleCalendarCellClick(data, dateOfMonth) : undefined)}
              gridClick={() => setCalendarDay(new Date(formattedDate.replace(/-/g, "/")))}
              selected={formatDate(new Date(formattedDate.replace(/-/g, "/"))) === formatDate(calendarDay)} today={formatDate(new Date(formattedDate.replace(/-/g, "/"))) === formatDate(new Date())}
              ref={el => (dayRefs.current[dateOfMonth] = el)}
              mobileView={isMobile}
            >
              {(data.length > 0 || notifications?.length > 0 || eventsOnDay.length > 0) && (
                <div
                  className={`calendar-iframe-calendar-badge-grouping${isMobile ? " mobile" : ""}${statusModal.isOpen && statusModal.activeDate === formattedDate && statusModal.type === "status"
                    ? " active"
                    : ""
                    }`}
                  onClick={() => (isMobile ? handleCalendarCellClick(data, dateOfMonth) : undefined)}
                >
                  {data.length > 0 && (
                    <Badge
                      className={`status-badge event${statusModal.isOpen && statusModal.activeDate === formattedDate && statusModal.type === "status"
                        ? " active"
                        : ""
                        }`}
                      size="small"
                      onClick={() => handleCalendarCellClick(data, dateOfMonth)}
                    >
                      {isMobile ? <FontAwesomeIcon icon={faBell} /> : t("Daily Updates")}
                    </Badge>
                  )}
                  {eventsOnDay.map(event => (
                    <Badge
                      className={`status-badge event${statusModal.isOpen && statusModal.activeDate === formattedDate && statusModal.type === "status"
                        ? " active"
                        : ""
                        }`}
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        handleEventClick(event, dateOfMonth);
                      }}
                      key={event.event_id}
                    >
                      {isMobile ? <FontAwesomeIcon icon={faTrophy} /> : event.event_name}
                    </Badge>
                  ))}
                  {notifications?.map(notification => {
                    const isSunday = moment(formattedDate).day() === 0;
                    const isFirstDay = moment(notification.start_date).isSame(moment(formattedDate), "day");
                    const showText = isFirstDay || isSunday;

                    return (
                      <ConnectedBadge
                        key={notification.id}
                        notification={notification}
                        formattedDate={formattedDate}
                        isMobile={isMobile}
                        onClick={e => {
                          e.stopPropagation();
                          handleNotificationClick(notification, dateOfMonth);
                        }}
                        onMouseEnter={() => setHoveredNotificationId(getNotificationKey(notification))}
                        onMouseLeave={() => setHoveredNotificationId(null)}
                        isActive={
                          statusModal.isOpen &&
                          statusModal.type === "updates" &&
                          getNotificationKey(statusModal.singleNotification) === getNotificationKey(notification)
                        }
                        isHovered={hoveredNotificationId === getNotificationKey(notification)}
                        showText={showText}
                      />
                    );
                  })}
                </div>
              )}
            </CalendarGridDay>
          );
        }}
      />

      <FacilityStatusInfo
        isOpen={statusModal.isOpen}
        close={closeStatusModal}
        dateString={statusModal.activeDate}
        gridRefElement={!isMobile && !smallWindowHeight ? statusModal.ref : undefined}
        iFrame={true}
        title={statusModal.type === "status" ? t("Daily Updates") : statusModal.type === "event" ? statusModal.event?.event_name : statusModal.singleNotification?.subject}
      >
        {statusModal.statusData
          .filter(val => val.label.toLowerCase().includes("note"))
          .map(status => (
            <div key={status.id} className="calendar-iframe-status-note">
              <p className="note-value">{status.value}</p>
            </div>
          ))
        }
        {!statusModal.singleNotification && !statusModal.event && (
          <DataTable
            columns={[
              { label: "", width: "50%" },
              { label: "", width: "50%" },
            ]}
            hideHeader
            className="calendar-iframe-calendar"
          >
            {statusModal.statusData
              .filter(val => !val.label.toLowerCase().includes("note"))
              .map(status => (
                <tr key={status.id} className="calendar-iframe-status-popup-table-row">
                  <td>{status.label}</td>
                  <td>{status.value}</td>
                </tr>
              ))
            }
          </DataTable>
        )}
        {statusModal.singleNotification && (
          <div className="calendar-iframe-notification">
            <p className="notification-value">{statusModal.singleNotification.content}</p>
          </div>
        )}
        {statusModal.event && (
          <div className="calendar-iframe-event">
            <Badge type="lightblue" size="medium" className="mb-2">
              {moment(statusModal.event.start_time as string, "HH:mm")?.format("LT")} -{" "}
              {moment(statusModal.event.end_time as string, "HH:mm")?.format("LT")}
            </Badge>
            <Markdown markdownText={statusModal.event.description} />
            {statusModal.event.registration_open ? (
              <p className="event-value">{window.origin + `/league/${facilityShortName}/${statusModal.event.handle}/register`}</p>
            ) : (
              <p className="event-value">{t("Registration is closed")}</p>
            )}
          </div>
        )}
      </FacilityStatusInfo>
    </div>
  );
}